import { ALGORITHM_CARDS, AlgorithmCardType, Tier } from '../../shared/types';
import type { AudioManager } from '../audio/AudioManager';

/**
 * Map every `AlgorithmCardType` to the audio method that voices its
 * activation. Each card has its own synth recipe in `AudioManager` so the
 * player can identify abilities by ear alone.
 */
const CARD_AUDIO_METHODS: Record<AlgorithmCardType, keyof AudioManager> = {
  [AlgorithmCardType.Regroup]: 'playCardActivation_Regroup',
  [AlgorithmCardType.Scatter]: 'playCardActivation_Scatter',
  [AlgorithmCardType.Follow]: 'playCardActivation_Follow',
  [AlgorithmCardType.Orbit]: 'playCardActivation_Orbit',
  [AlgorithmCardType.DefensiveWall]: 'playCardActivation_DefensiveWall',
  [AlgorithmCardType.RadarRing]: 'playCardActivation_RadarRing',
  [AlgorithmCardType.FocusFire]: 'playCardActivation_FocusFire',
  [AlgorithmCardType.RelayNet]: 'playCardActivation_RelayNet',
  [AlgorithmCardType.CageSurround]: 'playCardActivation_CageSurround',
  [AlgorithmCardType.SyncDive]: 'playCardActivation_SyncDive',
  [AlgorithmCardType.ConvertShot]: 'playCardActivation_ConvertShot',
  [AlgorithmCardType.ChoreoUltimate]: 'playCardActivation_ChoreoUltimate',
  [AlgorithmCardType.HorizontalOrbit]: 'playCardActivation_HorizontalOrbit',
  [AlgorithmCardType.DiagonalOrbit]: 'playCardActivation_DiagonalOrbit',
};

/**
 * Per-cardType throttle: a frame-perfect spam (e.g. holding "1" or
 * activating + discarding the same slot back-to-back) layers identical
 * activation envelopes which sounds like a glitch. We cap each cardType's
 * dispatch at one play per `THROTTLE_MS`. The gate is intentionally
 * narrow — it only suppresses the per-card dispatch path; other systems
 * (impacts, hits, BGM) call the AudioManager directly and are unaffected.
 *
 * State is module-local so the throttle survives across React/Hud
 * re-renders. Tests reset it via {@link _resetCardAudioThrottle}.
 */
const THROTTLE_MS = 50;
const lastPlayedAt = new Map<AlgorithmCardType, number>();

function nowMs(): number {
  // performance.now() is monotonic; fall back to Date.now() for
  // environments without performance (older test runners, tiny shims).
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/** Test-only: clear the per-cardType throttle map. */
export function _resetCardAudioThrottle(): void {
  lastPlayedAt.clear();
}

/**
 * Route a CardActivated event to the correct per-card synthesizer call.
 * Falls back to the legacy tier-based dispatch if the cardType is missing
 * or out of range. Drops repeat dispatches for the same `cardType` within
 * {@link THROTTLE_MS} so frame-perfect spam doesn't layer envelopes.
 */
export function playCardActivationByType(
  audio: AudioManager | null,
  cardType: number | undefined,
): void {
  if (!audio) return;
  const method = cardType !== undefined
    ? CARD_AUDIO_METHODS[cardType as AlgorithmCardType]
    : undefined;
  if (!method || typeof audio[method] !== 'function') {
    // Unknown cardType — fall back to tier-based dispatch.
    playCardActivationByTier(audio, cardType);
    return;
  }
  const key = cardType as AlgorithmCardType;
  const t = nowMs();
  const last = lastPlayedAt.get(key);
  if (last !== undefined && t - last < THROTTLE_MS) return;
  lastPlayedAt.set(key, t);
  (audio[method] as () => void).call(audio);
}

/**
 * @deprecated Tier-based dispatch — kept as a safety net for unknown card
 * types and for any legacy call sites that still reach for it. New code
 * should call {@link playCardActivationByType} so each card gets its own
 * unique sound.
 */
export function playCardActivationByTier(
  audio: AudioManager | null,
  cardType: number | undefined,
): void {
  if (!audio) return;
  const card = cardType !== undefined
    ? ALGORITHM_CARDS[cardType as AlgorithmCardType]
    : undefined;
  if (card?.tier === Tier.Common) {
    audio.playCommonCardSound();
  } else if (card?.tier === Tier.Rare) {
    audio.playRareCardSound();
  } else if (card?.tier === Tier.Legend) {
    audio.playLegendCardSound();
  } else {
    audio.playCardActivate();
  }
}
